/**
 * ============================================
 * UCM - Ultra Car Manager
 * Public Footer - Premium Version
 * © 2024 AlbaSoft Studio - All Rights Reserved
 * ============================================
 */

import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { useShowroom } from './PublicLayout';

export default function PublicFooter() {
  const { settings } = useShowroom();
  const [email, setEmail] = useState('');
  const [subscribed, setSubscribed] = useState(false);

  const handleNewsletter = (e: React.FormEvent) => {
    e.preventDefault();
    setSubscribed(true);
    setEmail('');
  };

  const currentYear = new Date().getFullYear();

  return (
    <footer className="bg-slate-900 text-white">
      {/* Newsletter Section */}
      <div className="bg-gradient-to-r from-red-600 via-red-700 to-slate-900 shadow-2xl shadow-red-900/50">
        <div className="container mx-auto px-4 py-10">
          <div className="flex flex-col md:flex-row items-center justify-between gap-6">
            <div>
              <h3 className="text-2xl font-bold mb-1 drop-shadow-lg">Rimani aggiornato</h3>
              <p className="text-red-100">Iscriviti alla newsletter per ricevere le migliori offerte</p>
            </div>
            <form onSubmit={handleNewsletter} className="flex gap-2 w-full md:w-auto">
              {subscribed ? (
                <div className="flex items-center gap-2 text-white bg-white/20 px-6 py-3 rounded-lg">
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                  <span>Grazie per l'iscrizione!</span>
                </div>
              ) : (
                <>
                  <input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="La tua email"
                    required
                    className="flex-1 md:w-72 px-4 py-3 rounded-lg text-slate-900 focus:outline-none focus:ring-2 focus:ring-white/50"
                  />
                  <button
                    type="submit"
                    className="px-6 py-3 bg-slate-900 text-white rounded-lg font-medium hover:bg-slate-800 transition-colors"
                  >
                    Iscriviti
                  </button>
                </>
              )}
            </form>
          </div>
        </div>
      </div>

      {/* Main Footer */}
      <div className="container mx-auto px-4 py-12">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 lg:gap-12">
          {/* Company Info */}
          <div className="lg:col-span-1">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-12 h-12 bg-gradient-to-br from-red-600 to-red-700 rounded-xl flex items-center justify-center shadow-lg shadow-red-600/30">
                <svg className="w-7 h-7 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8.25 18.75a1.5 1.5 0 01-3 0m3 0a1.5 1.5 0 00-3 0m3 0h6m-9 0H3.375a1.125 1.125 0 01-1.125-1.125V14.25m17.25 4.5a1.5 1.5 0 01-3 0m3 0a1.5 1.5 0 00-3 0m3 0h1.125c.621 0 1.129-.504 1.09-1.124a17.902 17.902 0 00-3.213-9.193 2.056 2.056 0 00-1.58-.86H14.25M16.5 18.75h-2.25m0-11.177v-.958c0-.568-.422-1.048-.987-1.106a48.554 48.554 0 00-10.026 0 1.106 1.106 0 00-.987 1.106v7.635m12-6.677v6.677m0 4.5v-4.5m0 0h-12" />
                </svg>
              </div>
              <div>
                <span className="font-bold text-xl">{settings?.general?.siteName || 'AutoSalone'}</span>
                <span className="text-slate-400 text-sm block">Premium Cars</span>
              </div>
            </div>
            <p className="text-slate-400 text-sm mb-4">
              Da oltre 20 anni il tuo partner di fiducia per l'acquisto di auto usate garantite.
              Qualita, trasparenza e servizio al cliente sono i nostri valori.
            </p>
            {/* Social Icons */}
            <div className="flex gap-3">
              {settings?.social?.facebook && (
                <a href={settings.social.facebook} target="_blank" rel="noopener noreferrer" title="Seguici su Facebook" aria-label="Seguici su Facebook" className="w-10 h-10 rounded-lg bg-slate-800 flex items-center justify-center hover:bg-blue-600 transition-colors">
                  <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                    <path d="M18.77,7.46H14.5v-1.9c0-.9.6-1.1,1-1.1h3V.5L14.17.5C10.24.5,9.5,3.44,9.5,5.32V7.46H6.5v4h3V22h5V11.46h3.85Z"/>
                  </svg>
                </a>
              )}
              {settings?.social?.instagram && (
                <a href={settings.social.instagram} target="_blank" rel="noopener noreferrer" title="Seguici su Instagram" aria-label="Seguici su Instagram" className="w-10 h-10 rounded-lg bg-slate-800 flex items-center justify-center hover:bg-gradient-to-br hover:from-purple-600 hover:to-pink-600 transition-colors">
                  <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                    <path d="M12,2.16c3.2,0,3.58,0,4.85.07,3.25.15,4.77,1.69,4.92,4.92.06,1.27.07,1.65.07,4.85s0,3.58-.07,4.85c-.15,3.23-1.66,4.77-4.92,4.92-1.27.06-1.65.07-4.85.07s-3.58,0-4.85-.07c-3.26-.15-4.77-1.7-4.92-4.92-.06-1.27-.07-1.65-.07-4.85s0,3.58.07-4.85C2.38,3.92,3.9,2.38,7.15,2.23,8.42,2.18,8.8,2.16,12,2.16ZM12,0C8.74,0,8.33,0,7.05.07c-4.35.2-6.78,2.62-7,7C0,8.33,0,8.74,0,12S0,15.67.07,17c.2,4.36,2.62,6.78,7,7C8.33,24,8.74,24,12,24s3.67,0,4.95-.07c4.35-.2,6.78-2.62,7-7C24,15.67,24,15.26,24,12s0-3.67-.07-4.95c-.2-4.35-2.62-6.78-7-7C15.67,0,15.26,0,12,0Zm0,5.84A6.16,6.16,0,1,0,18.16,12,6.16,6.16,0,0,0,12,5.84ZM12,16a4,4,0,1,1,4-4A4,4,0,0,1,12,16ZM18.41,4.15a1.44,1.44,0,1,0,1.44,1.44A1.44,1.44,0,0,0,18.41,4.15Z"/>
                  </svg>
                </a>
              )}
            </div>
          </div>

          {/* Quick Links */}
          <div>
            <h4 className="font-bold text-lg mb-4">Link Rapidi</h4>
            <ul className="space-y-2.5">
              <li><Link to="/vetrina/noleggio" className="text-slate-400 hover:text-red-400 transition-colors">Noleggio Auto</Link></li>
              <li><Link to="/vetrina/vendita" className="text-slate-400 hover:text-red-400 transition-colors">Auto in Vendita</Link></li>
              <li><Link to="/vetrina/chi-siamo" className="text-slate-400 hover:text-red-400 transition-colors">Chi Siamo</Link></li>
              <li><Link to="/vetrina/contatti" className="text-slate-400 hover:text-red-400 transition-colors">Contattaci</Link></li>
            </ul>
          </div>

          {/* Services */}
          <div>
            <h4 className="font-bold text-lg mb-4">Servizi</h4>
            <ul className="space-y-2.5">
              <li><Link to="/vetrina/servizi/finanziamenti" className="text-slate-400 hover:text-red-400 transition-colors">Finanziamenti</Link></li>
              <li><Link to="/vetrina/servizi/garanzia" className="text-slate-400 hover:text-red-400 transition-colors">Garanzia Estesa</Link></li>
              <li><Link to="/vetrina/servizi/permuta" className="text-slate-400 hover:text-red-400 transition-colors">Permuta Auto</Link></li>
              <li><Link to="/vetrina/servizi/pratiche" className="text-slate-400 hover:text-red-400 transition-colors">Pratiche Auto</Link></li>
            </ul>
          </div>

          {/* Contact Info */}
          <div>
            <h4 className="font-bold text-lg mb-4">Contattaci</h4>
            <ul className="space-y-3">
              <li className="flex items-start gap-3">
                <svg className="w-5 h-5 text-red-500 mt-0.5 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                </svg>
                <div className="text-slate-400 text-sm">
                  {settings?.contact?.address || 'Via Roma 1'}<br />
                  {settings?.contact?.cap || '20100'} {settings?.contact?.city || 'Milano'} ({settings?.contact?.province || 'MI'})
                </div>
              </li>
              <li>
                <a href={`tel:${settings?.contact?.phone || ''}`} className="flex items-center gap-3 text-slate-400 hover:text-red-400 transition-colors">
                  <svg className="w-5 h-5 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                  </svg>
                  <span className="text-sm">{settings?.contact?.phone || '+39 02 1234567'}</span>
                </a>
              </li>
              <li>
                <a href={`mailto:${settings?.contact?.email || ''}`} className="flex items-center gap-3 text-slate-400 hover:text-red-400 transition-colors">
                  <svg className="w-5 h-5 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                  </svg>
                  <span className="text-sm">{settings?.contact?.email || 'info@demo.local'}</span>
                </a>
              </li>
            </ul>
          </div>
        </div>
      </div>

      {/* Bottom Bar */}
      <div className="border-t border-slate-800">
        <div className="container mx-auto px-4 py-4">
          <div className="flex flex-col md:flex-row items-center justify-between gap-4 text-sm text-slate-500">
            <div>
              © {currentYear} {settings?.general?.siteName || 'AutoSalone'}. Tutti i diritti riservati.
            </div>
            <div className="flex items-center gap-4">
              <Link to="/vetrina/privacy" className="hover:text-slate-300 transition-colors">Privacy Policy</Link>
              <Link to="/vetrina/cookie" className="hover:text-slate-300 transition-colors">Cookie Policy</Link>
              <Link to="/vetrina/termini" className="hover:text-slate-300 transition-colors">Termini e Condizioni</Link>
            </div>
            <div className="flex items-center gap-2 text-slate-600">
              <span>Powered by</span>
              <span className="font-semibold text-slate-400">UCM Premium</span>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
}
